
import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import DataCancelamentoUc from './DataCancelamentoUc';
import { rotuloCancelamentoUc } from '../../src/lib/dataCancelamentoUc';
import {
    Search,
    Download,
    MoreVertical,
    ChevronUp,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    Ban,
    FileEdit,
    AlertTriangle,
    CheckCircle2,
    Home,
    Zap,
    TrendingUp,
    MapPin,
    Building2,
    History,
    FilePlus2,
    Plus,
    FileX2,
    FileWarning,
    FileText,
    Banknote,
    Eye,
    X,
    Filter,
    Columns3,
    Pencil,
    Sun
} from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { buscarTudoPorChave } from '../../src/lib/paginacaoKeyset';
import { UnidadeConsumidora } from '../../types';
import { descreverConsumoUc, explicarAusenciaDeHistorico } from '../../src/lib/consumoOperacional';
import { formatDate } from '../../src/lib/utils';
import { logAction, logUsageEvent } from '../../src/lib/logger';
import { useAuth } from '../../src/contexts/AuthContext';
import { podeAcessarCancelamentos } from '../../src/lib/acessoCancelamento';
import { useToast } from '../../src/contexts/ToastContext';
import UnidadeModal from './UnidadeModal';
import CancelUCModal from './CancelUCModal';
import AditivoContratualModal from './AditivoContratualModal';
import ComunicadoAdvertenciaModal from './ComunicadoAdvertenciaModal';
import NotificacaoMoraModal from './NotificacaoMoraModal';
import TermCancelHistoryModal, { DocCancelavel } from './TermCancelHistoryModal';
import UCManagementModal, { getStatusBadgeColors } from './UCManagementModal';
import ExportModal, { ExportColumn } from '../../src/components/ExportModal';
import LoadingSpinner from '../../src/components/LoadingSpinner';
import { useEtiquetasUc, podeUsarEtiquetas } from './lib/useEtiquetasUc';
import { EtiquetasDaUc } from './components/EtiquetaUcPill';
import { useIndiceUsinas } from '../../src/hooks/useIndiceUsinas';
import { usinaDaUc, NOME_SEM_USINA, type UsinaVigor } from '../../src/lib/financeiro/usinaDaFatura';
import EtiquetasUcModal from './components/EtiquetasUcModal';
import { podeEditarCadastroDaUc } from '../../src/lib/permissaoCadastro';

const LOCALIZACAO_NAO_INFORMADA = '__nao_informado__';
const SEM_USINA = '__sem_usina__';

// --- Colunas configuráveis da tabela ---
// "UC / Cliente" e "Ações" são fixas; as demais o usuário escolhe exibir ou não,
// e a escolha fica salva no navegador (localStorage) entre sessões.
type ColumnKey = 'localizacao' | 'concessionaria' | 'perfil' | 'consumo' | 'consumo_observado' | 'situacao' | 'usina' | 'status_financeiro' | 'email' | 'whatsapp' | 'cpf_titular';

const COLUMN_META: { key: ColumnKey; label: string; sortKey: string }[] = [
    { key: 'localizacao', label: 'Localização', sortKey: 'cidade' },
    { key: 'concessionaria', label: 'Concessionária', sortKey: 'concessionaria' },
    { key: 'perfil', label: 'Perfil', sortKey: 'grupo_tarifario' },
    { key: 'consumo', label: 'Consumo Médio', sortKey: 'consumo_medio_kwh' },
    // Coluna informativa: o total da carteira e o filtro de consumo continuam
    // no campo de cadastro, então nenhum KPI muda de base. Aqui o operador vê o
    // consumo REAL ao lado do contratado.
    { key: 'consumo_observado', label: 'Consumo Observado', sortKey: 'consumo_observado_kwh' },
    { key: 'situacao', label: 'Situação', sortKey: 'situacao' },
    { key: 'usina', label: 'Usina', sortKey: 'usina' },
    { key: 'status_financeiro', label: 'Status Financeiro', sortKey: 'status_financeiro' },
    { key: 'email', label: 'E-mail', sortKey: 'email_cliente' },
    { key: 'whatsapp', label: 'WhatsApp', sortKey: 'whatsapp_cliente' },
    { key: 'cpf_titular', label: 'CPF/CNPJ Titular', sortKey: 'cpf_cnpj_titular' },
];

const DEFAULT_VISIBLE_COLUMNS: Record<ColumnKey, boolean> = {
    localizacao: true,
    concessionaria: true,
    perfil: true,
    consumo: true,
    consumo_observado: true,
    situacao: true,
    usina: true,
    status_financeiro: false,
    email: false,
    whatsapp: false,
    cpf_titular: false,
};

const COLUMNS_STORAGE_KEY = 'vigorhub:ucs-list:colunas-visiveis:v1';

const UnidadesList = () => {
    const [unidades, setUnidades] = useState<UnidadeConsumidora[]>([]);
    const [loading, setLoading] = useState(true);
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({ key: 'nome_cliente', direction: 'asc' });

    // Filters
    // `?busca=` semeia a busca: é o que faz o "Gerenciar UC" da tela de
    // Cancelamentos abrir já no cliente da linha, em vez da lista inteira (#6121).
    const [searchParams] = useSearchParams();
    const [searchTerm, setSearchTerm] = useState(() => searchParams.get('busca') ?? '');
    // Com a tela já aberta, um novo link com outro `?busca=` precisa trocar a busca.
    const buscaDaUrl = searchParams.get('busca');
    useEffect(() => { if (buscaDaUrl !== null) setSearchTerm(buscaDaUrl); }, [buscaDaUrl]);
    const [locationFilter, setLocationFilter] = useState('');
    const [concessionariaFilter, setConcessionariaFilter] = useState('');
    const [perfilFilter, setPerfilFilter] = useState('');
    const [situacaoFilter, setSituacaoFilter] = useState('');
    const [consumoMin, setConsumoMin] = useState('');
    const [consumoMax, setConsumoMax] = useState('');
    // Empresa faturadora (Vigor direto vs. empresa parceira) — '' = todas, '__vigor__' = só diretas.
    const [empresaParceiraFilter, setEmpresaParceiraFilter] = useState('');
    // '' = todas; SEM_USINA = sem usina da Vigor; senão, id da usina (#6402).
    const [usinaFilter, setUsinaFilter] = useState('');
    const indiceUsinas = useIndiceUsinas();
    // Usina de cada UC num mapa à parte, e não como campo na linha: a linha vai inteira
    // para o formulário de edição, que grava o objeto — campo inventado viraria coluna
    // desconhecida no update.
    const usinaPorUc = useMemo(() => {
        const mapa = new Map<number, UsinaVigor>();
        for (const u of unidades) {
            const usina = usinaDaUc(u, indiceUsinas);
            if (usina) mapa.set(u.id, usina);
        }
        return mapa;
    }, [unidades, indiceUsinas]);
    const [empresasParceiras, setEmpresasParceiras] = useState<{ id: string; nome: string }[]>([]);
    // Painel de filtros começa recolhido para não ocupar espaço vertical — abre sob demanda.
    const [filtersOpen, setFiltersOpen] = useState(false);

    // Colunas visíveis na tabela — persiste no localStorage do navegador.
    const [visibleColumns, setVisibleColumns] = useState<Record<ColumnKey, boolean>>(() => {
        try {
            const raw = localStorage.getItem(COLUMNS_STORAGE_KEY);
            if (raw) return { ...DEFAULT_VISIBLE_COLUMNS, ...JSON.parse(raw) };
        } catch { /* localStorage indisponível — segue com o padrão */ }
        return DEFAULT_VISIBLE_COLUMNS;
    });
    const [showColumnsMenu, setShowColumnsMenu] = useState(false);

    // Edição inline de célula (hoje: e-mail e WhatsApp) sem precisar abrir o modal de gestão da UC.
    const [editingCell, setEditingCell] = useState<{ ucId: number; field: 'email_cliente' | 'whatsapp_cliente' } | null>(null);
    const [editingValue, setEditingValue] = useState('');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 25;

    // Metrics
    const [activeUCsCount, setActiveUCsCount] = useState(0);
    const [avgConsumption, setAvgConsumption] = useState(0);
    const [totalEconomy, setTotalEconomy] = useState(0);

    // Modal States
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingUnidade, setEditingUnidade] = useState<UnidadeConsumidora | null>(null);
    // "Cancelar UC" = soft-cancel (situacao -> 'Cancelada'). Distinto do fluxo de Distrato (showCancelModal/cancelingUnidade).
    const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
    const [unidadeToCancel, setUnidadeToCancel] = useState<UnidadeConsumidora | null>(null);
    const [successModalOpen, setSuccessModalOpen] = useState(false);
    
    // Cancel State
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelingUnidade, setCancelingUnidade] = useState<UnidadeConsumidora | null>(null);

    // "Corrigir e gerar novamente": payload da geração anterior + id do log cancelado,
    // repassados ao modal de geração para ele nascer preenchido e registrar a substituição.
    // Um só objeto para os três documentos — só um pode estar aberto por vez.
    const [regeracao, setRegeracao] = useState<{ docTipo: DocCancelavel; payload: Record<string, any>; substituiLogId: number } | null>(null);

    // Aditivo Contratual State
    const [showAditivoModal, setShowAditivoModal] = useState(false);
    const [aditivoUnidade, setAditivoUnidade] = useState<UnidadeConsumidora | null>(null);

    // Comunicado de Advertência State
    const [showAdvertenciaModal, setShowAdvertenciaModal] = useState(false);
    const [advertenciaUnidade, setAdvertenciaUnidade] = useState<UnidadeConsumidora | null>(null);

    // Notificação de Mora State
    const [showMoraModal, setShowMoraModal] = useState(false);
    const [moraUnidade, setMoraUnidade] = useState<UnidadeConsumidora | null>(null);

    // Dropdown "Gerar Documento" (header)
    const [showDocsMenu, setShowDocsMenu] = useState(false);

    // History Cancel State
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [historyTerms, setHistoryTerms] = useState<any[]>([]);
    const [historyAdvertencias, setHistoryAdvertencias] = useState<any[]>([]);
    const [historyMoras, setHistoryMoras] = useState<any[]>([]);
    const [historyAditivos, setHistoryAditivos] = useState<any[]>([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    // Unidade atualmente aberta no histórico (para refazer o fetch após registrar um envio)
    const [historyUnidade, setHistoryUnidade] = useState<UnidadeConsumidora | null>(null);

    // UC Management Modal State
    const [ucModalOpen, setUcModalOpen] = useState(false);
    const [selectedUc, setSelectedUc] = useState<UnidadeConsumidora | null>(null);

    // Dropdown State
    const [dropdownState, setDropdownState] = useState<{
        unidade: UnidadeConsumidora;
        position: { top: number; left: number };
    } | null>(null);

    const { user, profile } = useAuth();
    const { addToast } = useToast();
    // Distrato vive em termo_cancelamento_logs, que a RLS só abre para adm/gerente/analista (#5826).
    const podeDistrato = podeAcessarCancelamentos(profile?.role);
    // Etiquetas de UC: as mesmas da lista de faturas (docs/superpowers/specs/2026-10-13-etiquetas-uc-design.md).
    const podeEtiquetar = podeUsarEtiquetas(profile?.role);
    const etiquetasApi = useEtiquetasUc(podeEtiquetar);
    const [ucEtiquetando, setUcEtiquetando] = useState<{ id: number; rotulo: string } | null>(null);

    const [showExportModal, setShowExportModal] = useState(false);

    const ucsExportColumns: ExportColumn[] = [
        { key: 'numero_uc', label: 'Número UC' },
        { key: 'nome_cliente', label: 'Nome Cliente' },
        { key: 'cpf_cnpj_titular', label: 'CPF/CNPJ Titular' },
        { key: 'titular_fatura', label: 'Titular Fatura' },
        { key: 'cidade', label: 'Cidade' },
        { key: 'estado', label: 'Estado' },
        { key: 'endereco', label: 'Endereço' },
        { key: 'bairro', label: 'Bairro' },
        { key: 'cep', label: 'CEP' },
        { key: 'concessionaria', label: 'Concessionária' },
        { key: 'grupo_tarifario', label: 'Grupo Tarifário' },
        { key: 'tipo_ligacao', label: 'Tipo Ligação' },
        // "Consumo Médio" continua sendo o rótulo do campo de cadastro em toda a
        // exportação — renomear quebraria as planilhas de quem já usa. O número
        // real entra como coluna nova ao lado, com o nº de faturas que o sustenta.
        { key: 'consumo_medio_kwh', label: 'Consumo Médio (kWh)' },
        { key: 'consumo_observado_kwh', label: 'Consumo Observado (kWh)' },
        { key: 'consumo_observado_meses', label: 'Faturas no Consumo Observado' },
        { key: 'rateio_unidade_geradora_id', label: 'Usina', format: (_v: any, row?: any) => usinaPorUc.get(row?.id)?.nome ?? '' },
        { key: 'unidade_geradora', label: 'Unidade Geradora' },
        { key: 'desconto_tarifa', label: 'Desconto Tarifa (%)' },
        { key: 'desconto_bandeira', label: 'Desconto Bandeira (%)' },
        { key: 'carencia', label: 'Carência' },
        { key: 'situacao', label: 'Situação' },
        // #6005: a data de cancelamento vai junto na planilha; "(estimada)" quando
        // veio do histórico e não de um carimbo feito na hora.
        { key: 'cancelada_em', label: 'Cancelada em', format: (v: any, row?: any) => {
            const r = rotuloCancelamentoUc({ situacao: row?.situacao, cancelada_em: v, cancelada_em_origem: row?.cancelada_em_origem });
            return r ? r.texto.replace(/^em /, '') + (r.estimada ? ' (estimada)' : '') : '';
        } },
        { key: 'status_financeiro', label: 'Status Financeiro' },
        { key: 'created_at', label: 'Data de Cadastro', format: (v: any) => v ? new Date(v).toLocaleDateString('pt-BR') : '' },
    ];

    const fetchData = async () => {
        setLoading(true);
        try {
            // Pagina por chave, não por .range(): OFFSET relê as páginas anteriores
            // a cada ida ao banco (ver src/lib/paginacaoKeyset.ts). A ordenação por
            // nome volta aqui, depois de montada a lista.
            const allUcs = (await buscarTudoPorChave<UnidadeConsumidora>(
                (apos, tamanho) => {
                    let q = supabase
                        .from('unidades_consumidoras')
                        .select('*')
                        .order('id', { ascending: true })
                        .limit(tamanho);
                    if (apos != null) q = q.gt('id', apos);
                    if (profile?.empresa_parceira_id) q = q.eq('empresa_parceira_id', profile.empresa_parceira_id);
                    return q as any;
                },
                (u) => (u as any).id,
            )).sort((a: any, b: any) => String(a.nome_cliente || '').localeCompare(String(b.nome_cliente || ''), 'pt-BR'));

            // Fetch Economy Data (from Boletos)
            const { data: boletosData, error: boletosError } = await supabase
                .from('boletos_vigor')
                .select('economia');

            if (boletosError) throw boletosError;

            const ucs = allUcs;
            setUnidades(ucs);

            // Calculate Metrics
            const activeCount = ucs.filter(u => u.situacao !== 'Cancelada' && u.situacao !== null).length;
            setActiveUCsCount(activeCount);

            // Average Consumption
            const totalConsumption = ucs.reduce((acc, u) => acc + (Number(u.consumo_medio_kwh) || 0), 0);
            setAvgConsumption(ucs.length > 0 ? totalConsumption / ucs.length : 0);

            // Total Economy
            const economySum = (boletosData || []).reduce((acc, b) => acc + (Number(b.economia) || 0), 0);
            setTotalEconomy(economySum);

        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        supabase.from('empresas_parceiras').select('id, nome').order('nome').then(({ data }) => setEmpresasParceiras(data || []));
    }, []);

    useEffect(() => {
        const handleClickOutside = () => setDropdownState(null);
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    useEffect(() => {
        try { localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(visibleColumns)); } catch { /* ignore */ }
    }, [visibleColumns]);

    // Reset pagination when search or sort config changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, sortConfig, locationFilter, concessionariaFilter, perfilFilter, situacaoFilter, consumoMin, consumoMax, empresaParceiraFilter, usinaFilter]);

    // --- Filter Options (derived from loaded data) ---
    const locationOptions = useMemo(() => {
        const set = new Set<string>();
        unidades.forEach(u => {
            if (u.cidade) set.add(`${u.cidade}/${u.estado || ''}`);
        });
        return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'));
    }, [unidades]);

    const hasUnidadesSemLocalizacao = useMemo(() => unidades.some(u => !u.cidade), [unidades]);

    const concessionariaOptions = useMemo(() => {
        const set = new Set<string>();
        unidades.forEach(u => { if (u.concessionaria) set.add(u.concessionaria); });
        return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'));
    }, [unidades]);

    const perfilOptions = useMemo(() => {
        const set = new Set<string>();
        unidades.forEach(u => { if (u.grupo_tarifario) set.add(u.grupo_tarifario); });
        return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'));
    }, [unidades]);

    const situacaoOptions = useMemo(() => {
        const set = new Set<string>();
        unidades.forEach(u => { if (u.situacao) set.add(u.situacao); });
        return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'));
    }, [unidades]);

    const activeFilterCount = [locationFilter, concessionariaFilter, perfilFilter, situacaoFilter, empresaParceiraFilter, usinaFilter, consumoMin, consumoMax].filter(Boolean).length;
    const hasActiveFilters = activeFilterCount > 0;

    const clearFilters = () => {
        setLocationFilter('');
        setConcessionariaFilter('');
        setPerfilFilter('');
        setSituacaoFilter('');
        setConsumoMin('');
        setConsumoMax('');
        setEmpresaParceiraFilter('');
        setUsinaFilter('');
    };

    // --- Colunas: visibilidade e edição inline ---
    const activeColumns = COLUMN_META.filter(c => visibleColumns[c.key]);
    const toggleColumn = (key: ColumnKey) => setVisibleColumns(prev => ({ ...prev, [key]: !prev[key] }));

    const startEdit = (e: React.MouseEvent, uc: UnidadeConsumidora, field: 'email_cliente' | 'whatsapp_cliente') => {
        e.stopPropagation();
        setEditingCell({ ucId: uc.id, field });
        setEditingValue(uc[field] || '');
    };

    const cancelEdit = () => {
        setEditingCell(null);
        setEditingValue('');
    };

    const saveEdit = async () => {
        if (!editingCell) return;
        const { ucId, field } = editingCell;
        const value = editingValue.trim() || null;
        const current = unidades.find(u => u.id === ucId);
        if (current && (current[field] || null) === value) {
            cancelEdit();
            return;
        }
        const { error } = await supabase.from('unidades_consumidoras').update({ [field]: value }).eq('id', ucId);
        if (error) {
            console.error('Erro ao salvar campo inline:', error);
            // A trava do banco (#6239) já explica o motivo na mensagem.
            addToast(error.code === '42501' ? error.message : 'Não foi possível salvar a alteração.', 'error', 8000);
            return;
        }
        setUnidades(prev => prev.map(u => u.id === ucId ? { ...u, [field]: value } : u));
        if (selectedUc?.id === ucId) setSelectedUc(prev => prev ? { ...prev, [field]: value } : prev);
        cancelEdit();
    };

    const renderEditableCell = (uc: UnidadeConsumidora, field: 'email_cliente' | 'whatsapp_cliente', placeholder: string) => {
        const isEditing = editingCell?.ucId === uc.id && editingCell.field === field;
        if (isEditing) {
            return (
                <input
                    autoFocus
                    type="text"
                    value={editingValue}
                    onChange={(e) => setEditingValue(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') e.currentTarget.blur();
                        if (e.key === 'Escape') cancelEdit();
                    }}
                    onBlur={saveEdit}
                    className="w-full px-2 py-1 text-xs border border-primary rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white outline-none"
                />
            );
        }
        const value = uc[field];
        // #6239: contato de UC em operação só o Financeiro ou adm altera.
        if (!podeEditarCadastroDaUc(profile, uc)) {
            return (
                <span className={`truncate ${value ? '' : 'text-gray-400 dark:text-gray-500 italic'}`} title={value ? undefined : 'Só o Financeiro altera os contatos desta UC'}>
                    {value || '—'}
                </span>
            );
        }
        return (
            <div
                onClick={(e) => startEdit(e, uc, field)}
                title="Clique para editar"
                className="group/cell inline-flex items-center gap-1.5 -mx-2 px-2 py-1 rounded-md hover:bg-primary/5 dark:hover:bg-primary/10 cursor-text max-w-full"
            >
                <span className={`truncate ${value ? '' : 'text-gray-400 dark:text-gray-500 italic'}`}>{value || placeholder}</span>
                <Pencil size={11} className="opacity-0 group-hover/cell:opacity-100 text-gray-400 shrink-0 transition-opacity" />
            </div>
        );
    };

    const renderColumnCell = (uc: UnidadeConsumidora, key: ColumnKey) => {
        switch (key) {
            case 'localizacao':
                return (
                    <div className="flex items-center gap-1">
                        <MapPin size={12} />
                        {uc.cidade}/{uc.estado}
                    </div>
                );
            case 'concessionaria':
                return (
                    <div className="flex items-center gap-1">
                        <Building2 size={12} />
                        {uc.concessionaria || '-'}
                    </div>
                );
            case 'perfil':
                return uc.grupo_tarifario ? (
                    <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded text-xs font-medium border dark:border-gray-700">
                        {uc.grupo_tarifario} / {uc.tipo_ligacao}
                    </span>
                ) : '-';
            case 'consumo':
                return uc.consumo_medio_kwh ? `${uc.consumo_medio_kwh} kWh` : '-';
            case 'consumo_observado': {
                // A porcentagem sozinha não diz o que fazer — o rótulo abaixo dela diz.
                // O cadastro já nasce ~16% acima por projeto, então só acima de 30% a
                // diferença significa que ele deixou de descrever o cliente.
                const d = descreverConsumoUc({
                    comercialKwh: uc.consumo_medio_kwh,
                    observadoKwh: uc.consumo_observado_kwh,
                    mesesObservados: Number(uc.consumo_observado_meses) || 0,
                });
                if (d.observadoKwh == null) {
                    const a = explicarAusenciaDeHistorico({
                        observadoEm: uc.consumo_observado_em,
                        diasDesdeCadastro: uc.created_at
                            ? Math.floor((Date.now() - new Date(uc.created_at).getTime()) / 86400000)
                            : null,
                        situacao: uc.situacao,
                    });
                    return (
                        // O motivo já vem por extenso e visível (era redundante repeti-lo
                        // em `title` — a tela do Rateio usa a mesma explicação como texto
                        // secundário, não como tooltip).
                        <span className="block max-w-[15rem]">
                            <span className="italic text-gray-400 dark:text-gray-500">sem histórico</span>
                            <span className={`block text-[11px] leading-tight mt-0.5 ${a.requerAtencao ? 'text-red-600 dark:text-red-400 font-medium' : 'text-gray-400 dark:text-gray-500'}`}>
                                {a.requerAtencao ? '! ' : ''}{a.texto}
                            </span>
                        </span>
                    );
                }
                return (
                    <span className="block">
                        <span className="tabular-nums">{d.observadoKwh.toLocaleString('pt-BR')} kWh</span>
                        <span className="block text-[11px] text-gray-400 dark:text-gray-500 leading-tight">
                            {d.rotuloObservado}
                            {Number(uc.consumo_observado_dispersao) > 0.6 && (
                                <span
                                    className="ml-1.5 font-semibold text-red-600 dark:text-red-400"
                                    title={`Os meses variam ${Math.round(Number(uc.consumo_observado_dispersao) * 100)}% entre si — sinal de fatura com leitura incorreta. O número continua válido para consulta, mas confira o histórico desta UC.`}
                                >
                                    instável
                                </span>
                            )}
                            {d.divergenciaPercent != null && (
                                <span className={d.destacar ? 'ml-1.5 font-semibold text-red-600 dark:text-red-400' : 'ml-1.5'}>
                                    {d.divergenciaPercent > 0 ? '+' : ''}{d.divergenciaPercent}%
                                </span>
                            )}
                        </span>
                    </span>
                );
            }
            case 'situacao':
                return (
                    <span className="inline-flex flex-col items-start gap-0.5">
                        <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase border ${getStatusBadgeColors(uc.situacao)}`}>
                            {uc.situacao}
                        </span>
                        <DataCancelamentoUc uc={uc} />
                    </span>
                );
            case 'status_financeiro':
                return uc.status_financeiro ? (
                    <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase border ${
                        uc.status_financeiro === 'Adimplente'
                            ? 'bg-emerald-50 text-emerald-600 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800'
                            : uc.status_financeiro === 'Inadimplente'
                                ? 'bg-red-50 text-red-600 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800'
                                : 'bg-gray-50 text-gray-500 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700'
                    }`}>
                        {uc.status_financeiro}
                    </span>
                ) : '-';
            case 'email':
                return renderEditableCell(uc, 'email_cliente', 'Adicionar e-mail');
            case 'whatsapp':
                return renderEditableCell(uc, 'whatsapp_cliente', 'Adicionar WhatsApp');
            case 'cpf_titular':
                return uc.cpf_cnpj_titular || '-';
            case 'usina': {
                const usina = usinaPorUc.get(uc.id);
                return usina ? (
                    <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-semibold text-amber-800 dark:text-amber-300">
                        <Sun size={12} className="shrink-0" />
                        {usina.nome}
                    </span>
                ) : <span className="text-gray-400 dark:text-gray-500">-</span>;
            }
            default:
                return null;
        }
    };

    // --- Filtering Logic ---
    const filteredUnidades = useMemo(() => {
        let filtered = unidades;

        if (locationFilter === LOCALIZACAO_NAO_INFORMADA) {
            filtered = filtered.filter(uc => !uc.cidade);
        } else if (locationFilter) {
            filtered = filtered.filter(uc => `${uc.cidade}/${uc.estado || ''}` === locationFilter);
        }
        if (concessionariaFilter) {
            filtered = filtered.filter(uc => uc.concessionaria === concessionariaFilter);
        }
        if (perfilFilter) {
            filtered = filtered.filter(uc => uc.grupo_tarifario === perfilFilter);
        }
        if (situacaoFilter) {
            filtered = filtered.filter(uc => uc.situacao === situacaoFilter);
        }
        if (empresaParceiraFilter === '__vigor__') {
            filtered = filtered.filter(uc => !uc.empresa_parceira_id);
        } else if (empresaParceiraFilter) {
            filtered = filtered.filter(uc => uc.empresa_parceira_id === empresaParceiraFilter);
        }
        if (usinaFilter) {
            filtered = filtered.filter(uc => (usinaPorUc.get(uc.id)?.id ?? SEM_USINA) === usinaFilter);
        }
        const min = consumoMin.trim() ? Number(consumoMin) : null;
        const max = consumoMax.trim() ? Number(consumoMax) : null;
        if (min !== null && !Number.isNaN(min)) {
            filtered = filtered.filter(uc => (Number(uc.consumo_medio_kwh) || 0) >= min);
        }
        if (max !== null && !Number.isNaN(max)) {
            filtered = filtered.filter(uc => (Number(uc.consumo_medio_kwh) || 0) <= max);
        }

        // Apply Search Filter
        const search = searchTerm.trim().toLowerCase();
        
        // Helper to strip leading zeros, dashes, and dots for robust numeric matches
        const cleanNumericString = (val: string) => {
            const cleaned = val.replace(/[.\-\/]/g, '').replace(/\s/g, '');
            const withoutLeadingZeros = cleaned.replace(/^0+/, '');
            return withoutLeadingZeros || cleaned;
        };

        const searchCleaned = cleanNumericString(search);

        if (search) {
            filtered = filtered.filter(uc => {
                const nomeClienteMatch = (uc.nome_cliente || '').toLowerCase().includes(search);
                const titularFaturaMatch = (uc.titular_fatura || '').toLowerCase().includes(search);
                const cidadeMatch = (uc.cidade || '').toLowerCase().includes(search);
                const cpfClienteMatch = (uc.cpf_cnpj_cliente || '').includes(search) || cleanNumericString(uc.cpf_cnpj_cliente || '').includes(searchCleaned);
                const cpfTitularMatch = (uc.cpf_cnpj_titular || '').includes(search) || cleanNumericString(uc.cpf_cnpj_titular || '').includes(searchCleaned);
                
                // UC number matches: ignore leading zeros, dashes, dots
                const numeroUcMatch = String(uc.numero_uc || '').toLowerCase().includes(search) || 
                                     cleanNumericString(String(uc.numero_uc || '')).includes(searchCleaned);
                                     
                const outroNumeroUcMatch = String(uc.outro_numero_uc || '').toLowerCase().includes(search) || 
                                          cleanNumericString(String(uc.outro_numero_uc || '')).includes(searchCleaned);

                return nomeClienteMatch || titularFaturaMatch || cidadeMatch || cpfClienteMatch || cpfTitularMatch || numeroUcMatch || outroNumeroUcMatch;
            });
        }

        return filtered;
    }, [unidades, usinaPorUc, searchTerm, locationFilter, concessionariaFilter, perfilFilter, situacaoFilter, consumoMin, consumoMax, empresaParceiraFilter, usinaFilter]);

    // #6587: vindo da UC citada numa solicitação (?abrir=unico), abre a UC direto
    // quando a busca devolve uma só.
    const abrirUnicoRef = React.useRef(searchParams.get('abrir') === 'unico');
    useEffect(() => {
        if (!abrirUnicoRef.current || loading) return;
        abrirUnicoRef.current = false;
        if (filteredUnidades.length === 1) { setSelectedUc(filteredUnidades[0]); setUcModalOpen(true); }
    }, [loading, filteredUnidades]);

    const sortedUnidades = useMemo(() => {
        return [...filteredUnidades].sort((a, b) => {
            if (!sortConfig.key) return 0;
            //@ts-ignore
            const aValue = sortConfig.key === 'usina' ? usinaPorUc.get(a.id)?.nome : a[sortConfig.key];
            //@ts-ignore
            const bValue = sortConfig.key === 'usina' ? usinaPorUc.get(b.id)?.nome : b[sortConfig.key];

            if (aValue === bValue) return 0;
            if (aValue === undefined || aValue === null) return 1;
            if (bValue === undefined || bValue === null) return -1;

            const comparison = aValue < bValue ? -1 : 1;
            return sortConfig.direction === 'asc' ? comparison : -comparison;
        });
    }, [filteredUnidades, sortConfig, usinaPorUc]);

    const totalPages = Math.max(1, Math.ceil(sortedUnidades.length / ITEMS_PER_PAGE));

    const paginatedUnidades = useMemo(() => {
        const start = (currentPage - 1) * ITEMS_PER_PAGE;
        return sortedUnidades.slice(start, start + ITEMS_PER_PAGE);
    }, [sortedUnidades, currentPage]);

    const goToPage = (page: number | ((prev: number) => number)) => {
        setCurrentPage(prev => {
            const next = typeof page === 'function' ? page(prev) : page;
            return Math.max(1, Math.min(next, totalPages));
        });
    };

    const handleSort = (key: string) => {
        setSortConfig(current => ({
            key,
            direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const toggleDropdown = (e: React.MouseEvent, unidade: UnidadeConsumidora) => {
        e.stopPropagation();
        if (dropdownState?.unidade.id === unidade.id) {
            setDropdownState(null);
            return;
        }
        const rect = e.currentTarget.getBoundingClientRect();
        setDropdownState({
            unidade,
            position: { top: rect.bottom + 5, left: rect.right - 192 }
        });
    };

    const handleEdit = (unidade: UnidadeConsumidora) => {
        setEditingUnidade(unidade);
        setShowEditModal(true);
        setDropdownState(null);
    };

    const handleCancelConfirm = async () => {
        if (!unidadeToCancel || !user) return;
        try {
            const { error } = await supabase
                .from('unidades_consumidoras')
                .update({ situacao: 'Cancelada' })
                .eq('id', unidadeToCancel.id);
            if (error) throw error;

            logUsageEvent({ userId: user.id, modulo: 'financeiro', funcionalidade: 'ucs', acao: 'cancelar_uc', origem: 'acao' });
            await logAction(user.id, 'Cancelou Unidade Consumidora', {
                uc_id: unidadeToCancel.id,
                uc_number: unidadeToCancel.numero_uc,
                cliente: unidadeToCancel.nome_cliente
            });

            setCancelConfirmOpen(false);
            setSuccessModalOpen(true);
            fetchData();
            setTimeout(() => { setSuccessModalOpen(false); setUnidadeToCancel(null); }, 2000);
        } catch (error) {
            console.error('Error canceling UC:', error);
            addToast('Erro ao cancelar unidade.', 'error');
        }
    };

    const handleViewHistory = async (unidade: UnidadeConsumidora | null) => {
        setDropdownState(null);
        setLoadingHistory(true);
        setShowHistoryModal(true);
        setHistoryUnidade(unidade);

        const buscarLogs = async (tabela: string) => {
            let query = supabase
                .from(tabela)
                .select('*')
                .order('created_at', { ascending: false });

            if (unidade) query = query.eq('uc_id', unidade.id);

            const { data, error } = await query;
            if (error) throw error;
            return data || [];
        };

        try {
            const [distratos, advertencias, moras, aditivos] = await Promise.all([
                (podeDistrato ? buscarLogs('termo_cancelamento_logs') : Promise.resolve([])).catch(e => { console.error('Error fetching cancel history:', e); return []; }),
                buscarLogs('comunicado_advertencia_logs').catch(e => { console.error('Error fetching advertência history:', e); return []; }),
                buscarLogs('notificacao_mora_logs').catch(e => { console.error('Error fetching mora history:', e); return []; }),
                buscarLogs('termo_aditivo_logs').catch(e => { console.error('Error fetching aditivo history:', e); return []; }),
            ]);

            // Nome de quem gerou, registrou o envio, enviou lembrete e registrou não-envio: uma única consulta para os 4 históricos
            const userIds = Array.from(new Set(
                [...distratos, ...advertencias, ...moras, ...aditivos]
                    .flatMap((l: any) => [l.user_id, l.enviado_cliente_por, l.lembrete_enviado_por, l.nao_enviado_cliente_por, l.cancelado_por])
                    .filter(Boolean)
            ));

            const nomePorUsuario = new Map<string, string>();
            if (userIds.length > 0) {
                const { data: perfis } = await supabase
                    .from('profiles')
                    .select('id, full_name')
                    .in('id', userIds);
                (perfis || []).forEach((p: any) => nomePorUsuario.set(p.id, p.full_name));
            }

            const mapLog = (docTipo: 'distrato' | 'advertencia' | 'mora' | 'aditivo') => (log: any) => ({
                url_pdf: log.url_pdf,
                data_criacao: log.created_at,
                tipo_cliente: log.tipo_cliente,
                numero_termo: log.numero_termo
                    ? (log.numero_termo.startsWith('DOC-') ? log.numero_termo : `DOC-${log.numero_termo.substring(0, 6).toUpperCase()}`)
                    : `DOC-${log.id?.toString().padStart(6, '0')}`,
                enviado_por: (log.user_id && nomePorUsuario.get(log.user_id)) || null,
                // O aditivo troca a titularidade, então o associado do documento é o novo titular
                associado: log.cliente_nome || log.novo_titular || null,
                // Advertência (15 dias) e Notificação Extrajudicial (5 d.ú.) têm prazo/assinatura/envio manual
                prazo_final: log.prazo_final ?? null,
                resultado_verificacao: log.resultado_verificacao ?? null,
                enviado_cliente_em: log.enviado_cliente_em ?? null,
                assinado_em: log.assinado_em ?? null,
                id_autentique: log.id_autentique ?? null,
                log_id: log.id ?? null,
                doc_tipo: docTipo,
                enviado_cliente_por_nome: (log.enviado_cliente_por && nomePorUsuario.get(log.enviado_cliente_por)) || null,
                lembrete_enviado_em: log.lembrete_enviado_em ?? null,
                lembrete_enviado_por_nome: (log.lembrete_enviado_por && nomePorUsuario.get(log.lembrete_enviado_por)) || null,
                nao_enviado_cliente_em: log.nao_enviado_cliente_em ?? null,
                nao_enviado_cliente_motivo: log.nao_enviado_cliente_motivo ?? null,
                nao_enviado_cliente_por_nome: (log.nao_enviado_cliente_por && nomePorUsuario.get(log.nao_enviado_cliente_por)) || null,
                // Cancelamento (distrato / advertência / notificação; aditivo nunca tem)
                cancelado_em: log.cancelado_em ?? null,
                cancelado_motivo: log.cancelado_motivo ?? null,
                cancelado_acao: log.cancelado_acao ?? null,
                autentique_resultado: log.autentique_resultado ?? null,
                cancelado_por_nome: (log.cancelado_por && nomePorUsuario.get(log.cancelado_por)) || null,
                payload_geracao: log.payload_geracao ?? null,
                // Só o aditivo tem subtipo; nas outras tabelas vem undefined
                tipo_aditivo: log.tipo_aditivo ?? null,
            });

            setHistoryTerms(distratos.map(mapLog('distrato')));
            setHistoryAdvertencias(advertencias.map(mapLog('advertencia')));
            setHistoryMoras(moras.map(mapLog('mora')));
            setHistoryAditivos(aditivos.map(mapLog('aditivo')));
        } catch (error) {
            console.error('Error fetching document history:', error);
        } finally {
            setLoadingHistory(false);
        }
    };

    // Registra a data em que o emissor enviou manualmente o PDF assinado ao cliente.
    // A partir dela o banco calcula o prazo (5 d.ú. na mora / 15 dias corridos na advertência).
    const handleRegistrarEnvio = async (logId: number, docTipo: 'advertencia' | 'mora', dataISO: string) => {
        const tabela = docTipo === 'mora' ? 'notificacao_mora_logs' : 'comunicado_advertencia_logs';
        const { error } = await supabase
            .from(tabela)
            .update({ enviado_cliente_em: dataISO, enviado_cliente_por: user?.id ?? null })
            .eq('id', logId);
        if (error) {
            console.error('Erro ao registrar envio:', error);
            addToast('Não foi possível registrar o envio. Tente novamente.', 'error');
            return;
        }
        addToast('Envio registrado. O prazo começou a contar a partir da data informada.', 'success');
        logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'ucs', acao: 'registrar_envio_documento', origem: 'acao' });
        await handleViewHistory(historyUnidade);
    };

    // Lembrete manual de assinatura pendente ao procurador (e-mail + notificação in-app),
    // só disponível enquanto o status é "Aguardando assinatura" (ver TermCancelHistoryModal).
    const handleEnviarLembrete = async (logId: number, docTipo: 'advertencia' | 'mora') => {
        const { data, error } = await supabase.functions.invoke('enviar-lembrete-assinatura', {
            body: { log_id: logId, doc_tipo: docTipo },
        });
        if (error) {
            let message = error.message || 'Não foi possível enviar o lembrete.';
            try {
                const body = await error.context?.clone?.().json();
                if (body?.message) message = body.message;
            } catch { /* corpo não é JSON ou já foi consumido */ }
            addToast(message, 'error');
            return;
        }
        if (!data?.success) {
            addToast(data?.message || 'Não foi possível enviar o lembrete.', 'info');
            await handleViewHistory(historyUnidade);
            return;
        }
        addToast(`Lembrete enviado para ${data.procurador_nome || 'o representante legal'}.`, 'success');
        logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'ucs', acao: 'enviar_lembrete_assinatura', origem: 'acao' });
        await handleViewHistory(historyUnidade);
    };

    // Registra que o documento não será encaminhado ao associado, com o motivo —
    // alternativa a handleRegistrarEnvio para quando a Vigor decide não seguir com o envio.
    const handleRegistrarNaoEnvio = async (logId: number, docTipo: 'advertencia' | 'mora', motivo: string) => {
        const tabela = docTipo === 'mora' ? 'notificacao_mora_logs' : 'comunicado_advertencia_logs';
        const { error } = await supabase
            .from(tabela)
            .update({ nao_enviado_cliente_em: new Date().toISOString(), nao_enviado_cliente_por: user?.id ?? null, nao_enviado_cliente_motivo: motivo })
            .eq('id', logId);
        if (error) {
            console.error('Erro ao registrar não envio:', error);
            addToast('Não foi possível registrar. Tente novamente.', 'error');
            return;
        }
        addToast('Registrado: este documento não será encaminhado ao associado.', 'success');
        logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'ucs', acao: 'registrar_nao_envio_documento', origem: 'acao' });
        await handleViewHistory(historyUnidade);
    };

    // Cancela o documento no Autentique e no VigorHub. Quem decide entre apagar (ninguém
    // assinou) e anular (já assinado) é a edge function junto com o n8n, olhando o estado
    // real do documento — não esta tela. Devolve `true` só quando deu certo, para o modal
    // saber se pode fechar o formulário de motivo.
    const cancelarDocumento = async (logId: number, docTipo: DocCancelavel, motivo: string): Promise<boolean> => {
        const { data, error } = await supabase.functions.invoke('cancelar-documento-juridico', {
            body: { log_id: logId, doc_tipo: docTipo, motivo },
        });
        if (error) {
            // functions.invoke engole a causa em resposta não-2xx: o motivo real vem no context.
            let message = error.message || 'Não foi possível cancelar o documento.';
            try {
                const body = await (error as any)?.context?.clone?.().json();
                if (body?.message) message = body.message;
            } catch { /* corpo não é JSON ou já foi consumido */ }
            addToast(message, 'error');
            return false;
        }
        if (!data?.success) {
            addToast(data?.message || 'Não foi possível cancelar o documento.', 'info');
            await handleViewHistory(historyUnidade);
            return false;
        }
        addToast(
            data.acao === 'anular'
                ? 'Documento anulado. Ele foi marcado como recusado no Autentique e o PDF continua acessível.'
                : 'Documento excluído do Autentique. O link de assinatura não funciona mais.',
            'success',
        );
        if (data.aviso) addToast(data.aviso, 'warning');
        logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'ucs', acao: 'cancelar_documento_juridico', origem: 'acao' });
        await handleViewHistory(historyUnidade);
        return true;
    };

    // "Corrigir e gerar novamente": cancela o atual e reabre o modal de geração com o
    // payload da emissão anterior. O Autentique não deixa trocar o arquivo de um documento
    // existente (`updateDocument` só mexe em nome/configurações), então "editar" é
    // necessariamente cancelar + emitir de novo.
    const corrigirDocumento = async (term: any, motivo: string): Promise<boolean> => {
        const docTipo = term.doc_tipo as DocCancelavel;
        const ok = await cancelarDocumento(term.log_id, docTipo, motivo);
        if (!ok) return false;

        if (!term.payload_geracao) {
            // Documento emitido antes de 25/08/2026 não guardou o payload. O modal abre
            // pela UC, como sempre — só não vem preenchido com o que foi digitado antes.
            addToast('Este documento é anterior ao registro do formulário — o modal vai abrir em branco, preenchido pela UC.', 'info');
        }
        setRegeracao(term.payload_geracao
            ? { docTipo, payload: term.payload_geracao, substituiLogId: term.log_id }
            : null);

        setShowHistoryModal(false);
        const unidade = historyUnidade;
        if (docTipo === 'distrato') { setCancelingUnidade(unidade); setShowCancelModal(true); }
        else if (docTipo === 'advertencia') { setAdvertenciaUnidade(unidade); setShowAdvertenciaModal(true); }
        else { setMoraUnidade(unidade); setShowMoraModal(true); }
        return true;
    };

    const handleSave = () => {
        fetchData();
    };

    const handleUcFieldsUpdate = (fields: Partial<UnidadeConsumidora>) => {
        if (!selectedUc) return;
        const updated = { ...selectedUc, ...fields };
        setSelectedUc(updated);
        setUnidades(prev => prev.map(u => u.id === selectedUc.id ? updated : u));
    };

    const handleAdd = () => {
        setEditingUnidade(null);
        setShowEditModal(true);
    }

    if (loading) return <LoadingSpinner label="Carregando unidades..." />;

    return (
        <div className="animate-fade-in space-y-6 pb-12">
            {/* Header */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                <div>
                    <h2 className="text-2xl font-poppins font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <div className="p-2 bg-primary/10 rounded-lg text-primary">
                            <Home size={24} />
                        </div>
                        Gestão de UCs e Clientes
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Gerencie as Unidades Consumidoras, histórico de consumo e economia acumulada.
                    </p>
                </div>

                <div className="flex flex-wrap items-center gap-3">

                    <button
                        onClick={handleAdd}
                        className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors shadow-sm"
                    >
                        <Plus size={16} /> Nova UC
                    </button>
                    <button
                        onClick={() => setShowExportModal(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm"
                    >
                        <Download size={16} /> Exportar Excel
                    </button>
                    <button
                        onClick={() => handleViewHistory(null)}
                        className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm"
                    >
                        <History size={16} /> Ver Histórico
                    </button>
                    <div className="relative">
                        <button
                            onClick={() => setShowColumnsMenu(v => !v)}
                            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm"
                        >
                            <Columns3 size={16} /> Colunas <ChevronDown size={14} className={`transition-transform ${showColumnsMenu ? 'rotate-180' : ''}`} />
                        </button>
                        {showColumnsMenu && (
                            <>
                                <div className="fixed inset-0 z-40" onClick={() => setShowColumnsMenu(false)} />
                                <div className="absolute right-0 top-full mt-2 w-72 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 py-2 z-50 animate-fade-in max-h-96 overflow-y-auto">
                                    <p className="px-4 pb-2 pt-1 text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Colunas visíveis na tabela</p>
                                    {COLUMN_META.map(col => (
                                        <label
                                            key={col.key}
                                            className="flex items-center gap-2.5 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={visibleColumns[col.key]}
                                                onChange={() => toggleColumn(col.key)}
                                                className="rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary/40"
                                            />
                                            {col.label}
                                        </label>
                                    ))}
                                    <div className="mt-1 pt-1.5 px-4 border-t border-gray-100 dark:border-gray-700">
                                        <p className="text-[11px] text-gray-400 dark:text-gray-500">"UC / Cliente" e "Ações" ficam sempre visíveis.</p>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                    <div className="relative">
                        <button
                            onClick={() => setShowDocsMenu(v => !v)}
                            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm"
                        >
                            <FileText size={16} /> Gerar Documento <ChevronDown size={14} className={`transition-transform ${showDocsMenu ? 'rotate-180' : ''}`} />
                        </button>
                        {showDocsMenu && (
                            <>
                                <div className="fixed inset-0 z-40" onClick={() => setShowDocsMenu(false)} />
                                <div className="absolute right-0 top-full mt-2 w-64 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 py-1.5 z-50 animate-fade-in">
                                    <button
                                        onClick={() => { setAditivoUnidade(null); setShowAditivoModal(true); setShowDocsMenu(false); }}
                                        className="flex items-center gap-3 w-full px-4 py-2.5 text-sm font-semibold text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                                    >
                                        <FilePlus2 size={16} />
                                        <span className="text-left">
                                            Gerar Aditivo
                                            <span className="block text-[11px] font-normal text-gray-400 dark:text-gray-500">Transferência, adição de UC ou desconto</span>
                                        </span>
                                    </button>
                                    <button
                                        onClick={() => { setAdvertenciaUnidade(null); setShowAdvertenciaModal(true); setShowDocsMenu(false); }}
                                        className="flex items-center gap-3 w-full px-4 py-2.5 text-sm font-semibold text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
                                    >
                                        <FileWarning size={16} />
                                        <span className="text-left">
                                            Gerar Advertência
                                            <span className="block text-[11px] font-normal text-gray-400 dark:text-gray-500">Violação de exclusividade</span>
                                        </span>
                                    </button>
                                    <button
                                        onClick={() => { setMoraUnidade(null); setShowMoraModal(true); setShowDocsMenu(false); }}
                                        className="flex items-center gap-3 w-full px-4 py-2.5 text-sm font-semibold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors"
                                    >
                                        <Banknote size={16} />
                                        <span className="text-left">
                                            Notificação Extrajudicial
                                            <span className="block text-[11px] font-normal text-gray-400 dark:text-gray-500">Cobrança de faturas em atraso</span>
                                        </span>
                                    </button>
                                    {podeDistrato && (<>
                                    <div className="my-1 border-t border-gray-100 dark:border-gray-700" />
                                    <button
                                        onClick={() => { setCancelingUnidade(null); setShowCancelModal(true); setShowDocsMenu(false); }}
                                        className="flex items-center gap-3 w-full px-4 py-2.5 text-sm font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                    >
                                        <AlertTriangle size={16} />
                                        <span className="text-left">
                                            Gerar Distrato
                                            <span className="block text-[11px] font-normal text-gray-400 dark:text-gray-500">Padrão ou inadimplência</span>
                                        </span>
                                    </button>
                                    </>)}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* UCs Ativas */}
                <div className="bg-white dark:bg-gray-800/40 p-6 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">UCs Ativas</p>
                        <p className="text-2xl font-montserrat font-bold mt-1 text-emerald-600 dark:text-emerald-400">{activeUCsCount}</p>
                    </div>
                    <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-500 rounded-xl">
                        <CheckCircle2 size={24} />
                    </div>
                </div>

                {/* Consumo Médio Global */}
                <div className="bg-white dark:bg-gray-800/40 p-6 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Consumo Médio Global</p>
                        <p className="text-2xl font-montserrat font-bold mt-1 text-gray-900 dark:text-white">
                            {Math.round(avgConsumption).toLocaleString('pt-BR')} <span className="text-sm font-normal text-gray-400">kWh</span>
                        </p>
                    </div>
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 text-blue-500 rounded-xl">
                        <Zap size={24} />
                    </div>
                </div>

                {/* Economia Total Entregue */}
                <div className="bg-white dark:bg-gray-800/40 p-6 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Economia Total Entregue</p>
                        <p className="text-2xl font-montserrat font-bold mt-1 text-gray-900 dark:text-white">
                            R$ {totalEconomy.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                    </div>
                    <div className="p-3 bg-amber-50 dark:bg-amber-900/20 text-amber-500 rounded-xl">
                        <TrendingUp size={24} />
                    </div>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="bg-white dark:bg-gray-800/40 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
                {/* Search + Filtros */}
                <div className="p-4 bg-gray-50/50 dark:bg-white/5 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <div className="relative flex-1 min-w-0">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                            <input
                                type="text"
                                placeholder="Buscar por cliente, UC ou cidade..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-12 pr-4 py-3 bg-white dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-white focus:border-primary outline-none transition-shadow focus:shadow-sm placeholder:text-gray-400 dark:placeholder:text-gray-500"
                            />
                        </div>

                        <div className="flex items-center gap-3 shrink-0 flex-wrap">
                            <button
                                onClick={() => setFiltersOpen(v => !v)}
                                className={`flex items-center gap-2 px-4 py-3 border rounded-xl text-sm font-semibold transition-colors ${
                                    filtersOpen || hasActiveFilters
                                        ? 'bg-primary/10 border-primary/30 text-primary'
                                        : 'bg-white dark:bg-gray-800/60 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                                }`}
                            >
                                <Filter size={16} />
                                Filtros
                                {activeFilterCount > 0 && (
                                    <span className="min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-primary text-white text-[10px] font-bold">
                                        {activeFilterCount}
                                    </span>
                                )}
                                <ChevronDown size={14} className={`transition-transform ${filtersOpen ? 'rotate-180' : ''}`} />
                            </button>
                            <span className="text-xs text-gray-400 whitespace-nowrap">
                                {unidades.length} no banco · {filteredUnidades.length} filtrados
                            </span>
                            {hasActiveFilters && (
                                <button
                                    onClick={clearFilters}
                                    className="flex items-center gap-1 text-xs font-semibold text-gray-500 dark:text-gray-400 hover:text-red-500 transition-colors"
                                >
                                    <X size={14} /> Limpar
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Filter Selects — recolhidos por padrão para não ocupar espaço vertical */}
                    {filtersOpen && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 mt-3 animate-fade-in">
                            <select
                                value={locationFilter}
                                onChange={(e) => setLocationFilter(e.target.value)}
                                className="w-full px-4 py-3 bg-white dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-700 dark:text-gray-200 focus:border-primary outline-none transition-shadow focus:shadow-sm"
                            >
                                <option value="">Localização (todas)</option>
                                {locationOptions.map(loc => (
                                    <option key={loc} value={loc}>{loc}</option>
                                ))}
                                {hasUnidadesSemLocalizacao && (
                                    <option value={LOCALIZACAO_NAO_INFORMADA}>Não informado</option>
                                )}
                            </select>

                            <select
                                value={concessionariaFilter}
                                onChange={(e) => setConcessionariaFilter(e.target.value)}
                                className="w-full px-4 py-3 bg-white dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-700 dark:text-gray-200 focus:border-primary outline-none transition-shadow focus:shadow-sm"
                            >
                                <option value="">Concessionária (todas)</option>
                                {concessionariaOptions.map(c => (
                                    <option key={c} value={c}>{c}</option>
                                ))}
                            </select>

                            <select
                                value={perfilFilter}
                                onChange={(e) => setPerfilFilter(e.target.value)}
                                className="w-full px-4 py-3 bg-white dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-700 dark:text-gray-200 focus:border-primary outline-none transition-shadow focus:shadow-sm"
                            >
                                <option value="">Perfil de Consumo (todos)</option>
                                {perfilOptions.map(p => (
                                    <option key={p} value={p}>{p}</option>
                                ))}
                            </select>

                            <select
                                value={situacaoFilter}
                                onChange={(e) => setSituacaoFilter(e.target.value)}
                                className="w-full px-4 py-3 bg-white dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-700 dark:text-gray-200 focus:border-primary outline-none transition-shadow focus:shadow-sm"
                            >
                                <option value="">Situação (todas)</option>
                                {situacaoOptions.map(s => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>

                            {indiceUsinas.usinas.length > 0 && (
                                <select
                                    value={usinaFilter}
                                    onChange={(e) => setUsinaFilter(e.target.value)}
                                    className="w-full px-4 py-3 bg-white dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-700 dark:text-gray-200 focus:border-primary outline-none transition-shadow focus:shadow-sm"
                                >
                                    <option value="">Usina (todas)</option>
                                    {indiceUsinas.usinas.map(u => (
                                        <option key={u.id} value={u.id}>{u.nome}</option>
                                    ))}
                                    <option value={SEM_USINA}>{NOME_SEM_USINA}</option>
                                </select>
                            )}

                            {profile?.role === 'adm' && (
                                <select
                                    value={empresaParceiraFilter}
                                    onChange={(e) => setEmpresaParceiraFilter(e.target.value)}
                                    className="w-full px-4 py-3 bg-white dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-700 dark:text-gray-200 focus:border-primary outline-none transition-shadow focus:shadow-sm"
                                >
                                    <option value="">Empresa faturadora (todas)</option>
                                    <option value="__vigor__">Vigor Energy (direto)</option>
                                    {empresasParceiras.map(ep => (
                                        <option key={ep.id} value={ep.id}>{ep.nome}</option>
                                    ))}
                                </select>
                            )}

                            <input
                                type="number"
                                min={0}
                                inputMode="numeric"
                                placeholder="Consumo mín. (kWh)"
                                value={consumoMin}
                                onChange={(e) => setConsumoMin(e.target.value)}
                                className="w-full px-4 py-3 bg-white dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-700 dark:text-gray-200 focus:border-primary outline-none transition-shadow focus:shadow-sm placeholder:text-gray-400 dark:placeholder:text-gray-500"
                            />

                            <input
                                type="number"
                                min={0}
                                inputMode="numeric"
                                placeholder="Consumo máx. (kWh)"
                                value={consumoMax}
                                onChange={(e) => setConsumoMax(e.target.value)}
                                className="w-full px-4 py-3 bg-white dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-700 dark:text-gray-200 focus:border-primary outline-none transition-shadow focus:shadow-sm placeholder:text-gray-400 dark:placeholder:text-gray-500"
                            />
                        </div>
                    )}
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse" style={{ minWidth: `${Math.max(1000, 260 + (activeColumns.length + 2) * 140)}px` }}>
                        <thead>
                            <tr className="bg-gray-50/50 dark:bg-gray-800/60 text-gray-400 dark:text-gray-400 text-[10px] font-bold uppercase tracking-wider border-b border-gray-100 dark:border-gray-700">
                                <th className="px-6 py-4 cursor-pointer hover:text-primary transition-colors select-none" onClick={() => handleSort('nome_cliente')}>
                                    <div className="flex items-center gap-1">
                                        UC / Cliente
                                        {sortConfig.key === 'nome_cliente' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                                    </div>
                                </th>
                                {activeColumns.map(col => (
                                    <th key={col.key} className="px-6 py-4 cursor-pointer hover:text-primary transition-colors select-none" onClick={() => handleSort(col.sortKey)}>
                                        <div className="flex items-center gap-1">
                                            {col.label}
                                            {sortConfig.key === col.sortKey && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                                        </div>
                                    </th>
                                ))}
                                <th className="px-6 py-4 text-right">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                            {paginatedUnidades.map(uc => (
                                <tr key={uc.id} onClick={() => { setSelectedUc(uc); setUcModalOpen(true); }} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer">
                                    {/* UC / Cliente */}
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col">
                                            <div className="flex items-center gap-2 mb-0.5">
                                                <span className="text-[10px] font-bold text-primary bg-primary/5 px-1.5 rounded w-fit">
                                                    {uc.numero_uc}
                                                </span>
                                                {uc.outro_numero_uc && (
                                                    <span className="text-[10px] font-bold text-slate-600 bg-slate-100 dark:bg-slate-800 dark:text-slate-400 px-1.5 rounded w-fit">
                                                        {uc.outro_numero_uc}
                                                    </span>
                                                )}
                                                {uc.empresa_parceira_id && (
                                                    <span className="text-[10px] font-bold text-orange-700 bg-orange-100 dark:bg-orange-900/30 dark:text-orange-400 px-1.5 rounded w-fit" title="Empresa faturadora">
                                                        {empresasParceiras.find(ep => ep.id === uc.empresa_parceira_id)?.nome || 'Empresa parceira'}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="mb-1 empty:hidden">
                                                <EtiquetasDaUc
                                                    etiquetas={etiquetasApi.porUc.get(uc.id)}
                                                    ucCadastrada
                                                    onAbrir={podeEtiquetar ? () => setUcEtiquetando({ id: uc.id, rotulo: `UC ${uc.numero_uc}${uc.outro_numero_uc ? ` / ${uc.outro_numero_uc}` : ''} · ${uc.nome_cliente || ''}` }) : undefined}
                                                />
                                            </div>
                                            <div className="text-sm text-gray-900 dark:text-white">
                                                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 mr-1">Assinante:</span>
                                                <span className="font-bold">{uc.nome_cliente}</span>
                                            </div>
                                            {uc.titular_fatura && (
                                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                    <span className="font-semibold text-gray-400 dark:text-gray-500 mr-1">Titular da fatura:</span>
                                                    <span>{uc.titular_fatura}</span>
                                                </div>
                                            )}
                                        </div>
                                    </td>

                                    {/* Colunas configuráveis */}
                                    {activeColumns.map(col => (
                                        <td key={col.key} className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                                            {renderColumnCell(uc, col.key)}
                                        </td>
                                    ))}

                                    {/* Ações */}
                                    <td className="px-4 py-4 text-right" onClick={e => e.stopPropagation()}>
                                        <button
                                            onClick={(e) => toggleDropdown(e, uc)}
                                            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                        >
                                            <MoreVertical size={16} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {sortedUnidades.length === 0 && (
                        <div className="p-12 text-center text-gray-400 dark:text-gray-500">
                            <p>Nenhuma unidade encontrada.</p>
                        </div>
                    )}
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-4 border-t border-gray-100 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-800/50">
                        <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                            Mostrando {((currentPage - 1) * ITEMS_PER_PAGE) + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, filteredUnidades.length)} de {filteredUnidades.length}
                        </span>
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => goToPage(1)}
                                disabled={currentPage === 1}
                                className="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                title="Primeira página"
                            >
                                <ChevronsLeft size={16} />
                            </button>
                            <button
                                onClick={() => goToPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                title="Anterior"
                            >
                                <ChevronLeft size={16} />
                            </button>

                            {(() => {
                                const pages: (number | '...')[] = [];
                                if (totalPages <= 7) {
                                    for (let i = 1; i <= totalPages; i++) pages.push(i);
                                } else {
                                    pages.push(1);
                                    if (currentPage > 3) pages.push('...');
                                    const start = Math.max(2, currentPage - 1);
                                    const end = Math.min(totalPages - 1, currentPage + 1);
                                    for (let i = start; i <= end; i++) pages.push(i);
                                    if (currentPage < totalPages - 2) pages.push('...');
                                    pages.push(totalPages);
                                }
                                return pages.map((p, idx) =>
                                    p === '...' ? (
                                        <span key={`dots-${idx}`} className="px-1 text-gray-400 dark:text-gray-500 text-xs select-none">…</span>
                                    ) : (
                                        <button
                                            key={p}
                                            onClick={() => goToPage(p as number)}
                                            className={`min-w-[32px] h-8 rounded-lg text-xs font-bold transition-all ${currentPage === p
                                                    ? 'bg-primary text-white shadow-sm'
                                                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                                                }`}
                                        >
                                            {p}
                                        </button>
                                    )
                                );
                            })()}

                            <button
                                onClick={() => goToPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                className="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                title="Próxima"
                            >
                                <ChevronRight size={16} />
                            </button>
                            <button
                                onClick={() => goToPage(totalPages)}
                                disabled={currentPage === totalPages}
                                className="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                title="Última página"
                            >
                                <ChevronsRight size={16} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Edit Modal */}
            <UnidadeModal
                isOpen={showEditModal}
                onClose={() => setShowEditModal(false)}
                unidade={editingUnidade}
                onSave={handleSave}
            />

            {/* Cancel Modal */}
            <CancelUCModal
                isOpen={showCancelModal}
                onClose={() => { setShowCancelModal(false); setRegeracao(null); }}
                unidade={cancelingUnidade}
                regeracao={regeracao?.docTipo === 'distrato' ? regeracao : null}
            />

            {/* Aditivo Contratual Modal */}
            <AditivoContratualModal
                isOpen={showAditivoModal}
                onClose={() => setShowAditivoModal(false)}
                unidade={aditivoUnidade}
            />

            {/* Comunicado de Advertência Modal */}
            <ComunicadoAdvertenciaModal
                isOpen={showAdvertenciaModal}
                onClose={() => { setShowAdvertenciaModal(false); setRegeracao(null); }}
                unidade={advertenciaUnidade}
                regeracao={regeracao?.docTipo === 'advertencia' ? regeracao : null}
            />

            {/* Notificação de Mora Modal */}
            <NotificacaoMoraModal
                isOpen={showMoraModal}
                onClose={() => { setShowMoraModal(false); setRegeracao(null); }}
                unidade={moraUnidade}
                regeracao={regeracao?.docTipo === 'mora' ? regeracao : null}
            />

            {/* Cancel History Modal */}
            <TermCancelHistoryModal
                isOpen={showHistoryModal}
                onClose={() => setShowHistoryModal(false)}
                terms={historyTerms}
                podeVerDistratos={podeDistrato}
                advertencias={historyAdvertencias}
                moras={historyMoras}
                aditivos={historyAditivos}
                onRegistrarEnvio={handleRegistrarEnvio}
                onEnviarLembrete={handleEnviarLembrete}
                onRegistrarNaoEnvio={handleRegistrarNaoEnvio}
                onCancelarDocumento={cancelarDocumento}
                onCorrigirDocumento={corrigirDocumento}
            />

            {/* UC Management Modal */}
            <UCManagementModal
                isOpen={ucModalOpen}
                onClose={() => setUcModalOpen(false)}
                unidade={selectedUc}
                onSave={handleSave}
                onUcFieldsUpdate={handleUcFieldsUpdate}
            />

            {/* Actions Dropdown Portal */}
            {dropdownState && createPortal(
                <div
                    className="fixed z-50 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 py-1 animate-fade-in"
                    style={{ top: dropdownState.position.top, left: dropdownState.position.left }}
                    onClick={e => e.stopPropagation()}
                >
                    <button
                        onClick={() => { setSelectedUc(dropdownState.unidade); setUcModalOpen(true); setDropdownState(null); }}
                        className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                        <Eye size={15} className="text-gray-400" /> Ver Detalhes
                    </button>
                    <button
                        onClick={() => handleEdit(dropdownState.unidade)}
                        className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                        <FileEdit size={15} className="text-blue-500" /> Editar UC
                    </button>
                    <button
                        onClick={() => { setAditivoUnidade(dropdownState.unidade); setShowAditivoModal(true); setDropdownState(null); }}
                        className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                        <FilePlus2 size={15} className="text-blue-400" /> Gerar Aditivo
                    </button>
                    {podeDistrato && (
                    <button
                        onClick={() => { setCancelingUnidade(dropdownState.unidade); setShowCancelModal(true); setDropdownState(null); }}
                        className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                        <FileX2 size={15} className="text-amber-500" /> Gerar Distrato
                    </button>
                    )}
                    <button
                        onClick={() => { setAdvertenciaUnidade(dropdownState.unidade); setShowAdvertenciaModal(true); setDropdownState(null); }}
                        className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                        <FileWarning size={15} className="text-orange-500" /> Gerar Advertência
                    </button>
                    <button
                        onClick={() => { setMoraUnidade(dropdownState.unidade); setShowMoraModal(true); setDropdownState(null); }}
                        className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                        <Banknote size={15} className="text-rose-500" /> Notificação Extrajudicial
                    </button>
                    <button
                        onClick={() => handleViewHistory(dropdownState.unidade)}
                        className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                        <History size={15} className="text-gray-400" /> Ver Histórico
                    </button>
                    <div className="my-1 border-t border-gray-100 dark:border-gray-700" />
                    <button
                        onClick={() => { setUnidadeToCancel(dropdownState.unidade); setCancelConfirmOpen(true); setDropdownState(null); }}
                        className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
                    >
                        <Ban size={15} /> Cancelar UC
                    </button>
                </div>,
                document.body
            )}

            {/* Cancel Confirmation Modal (soft-cancel: situacao -> Cancelada) */}
            {cancelConfirmOpen && unidadeToCancel && createPortal(
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md p-6 m-4 text-center transform scale-100 transition-all">
                        <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 text-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Ban size={32} />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Cancelar UC?</h3>
                        <p className="text-gray-500 dark:text-gray-400 mb-8">
                            A unidade <strong>{unidadeToCancel.numero_uc}</strong> terá a situação alterada para <strong>Cancelada</strong>. O cadastro e o histórico são preservados, e você pode reativá-la depois editando a UC.
                        </p>
                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={() => setCancelConfirmOpen(false)}
                                className="px-6 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                Voltar
                            </button>
                            <button
                                onClick={handleCancelConfirm}
                                className="px-6 py-2.5 rounded-xl border-2 border-amber-500 text-amber-600 font-bold hover:bg-amber-50 dark:hover:bg-amber-900/10 transition-colors"
                            >
                                Sim, cancelar UC
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Success Modal */}
            {successModalOpen && createPortal(
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-8 m-4 text-center transform scale-100 transition-all">
                        <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                            <CheckCircle2 size={32} />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Sucesso!</h3>
                        <p className="text-gray-500">
                            A unidade foi cancelada corretamente.
                        </p>
                    </div>
                </div>,
                document.body
            )}

            {/* Export Modal */}
            <ExportModal
                isOpen={showExportModal}
                onClose={() => setShowExportModal(false)}
                title="Exportar UCs / Clientes"
                columns={ucsExportColumns}
                data={filteredUnidades}
                filename="UCs_Clientes"
                dateField="created_at"
                dateLabel="Data de Cadastro"
            />

            <EtiquetasUcModal
                key={ucEtiquetando?.id ?? 'fechado'}
                uc={ucEtiquetando}
                api={etiquetasApi}
                onFechar={() => setUcEtiquetando(null)}
            />

        </div>
    );
};

export default UnidadesList;
